import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import Sortable from "sortablejs";
import { Modal, Input, Tag } from "antd";
import { X, Trash2 } from "react-feather";

import { AppStrings } from "../../../../utils/constants/AppStrings";

import ClipPath from "../../../../assets/icons/ic_clip-path.svg";

import {
  type FutureCard,
  type FutureMarketOption,
  type FutureTab,
} from "./futuresDummyData";

interface FutureMarketCardsProps {
  tabs: FutureTab[];
  cardsByTabId: Record<string, FutureCard[]>;
  onAddCard: (tabId: string, options: FutureMarketOption[]) => void;
  onDeleteCard: (tabId: string, entityId: string) => void;
  onReorderCards: (tabId: string, orderedEntityIds: string[]) => void;
  marketOptions?: FutureMarketOption[];
  isMarketOptionsLoading?: boolean;
  isMarketOptionsError?: boolean;
  onAddCardOpenChange?: (open: boolean) => void;
  // Blocks starting another delete while a replace-all markets PATCH is in
  // flight — concurrent deletes on the same tab can clobber each other.
  isDeletingCard?: boolean;
}

interface CardTarget {
  tabId: string;
  card: FutureCard;
}

function destroySortable(instance: Sortable | null | undefined): void {
  try {
    instance?.destroy();
  } catch (error) {
    // Sortable throws if the tbody was already removed with its DOM.
    console.warn("Failed to destroy futures cards Sortable", error);
  }
}

function FutureMarketCards({
  tabs,
  cardsByTabId,
  onAddCard,
  onDeleteCard,
  onReorderCards,
  marketOptions = [],
  isMarketOptionsLoading = false,
  isMarketOptionsError = false,
  onAddCardOpenChange,
  isDeletingCard = false,
}: FutureMarketCardsProps): React.ReactElement {
  const [addCardTabId, setAddCardTabId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<FutureMarketOption[]>(
    []
  );

  const isOptionSelected = (entityId: string) =>
    selectedOptions.some((option) => option.entity_id === entityId);

  const toggleOption = (option: FutureMarketOption) => {
    setSelectedOptions((prev) =>
      prev.some((selected) => selected.entity_id === option.entity_id)
        ? prev.filter((selected) => selected.entity_id !== option.entity_id)
        : [...prev, option]
    );
  };

  // Markets already on the open tab are matched by UOF identity (market id +
  // specifier), so options that map to an existing card can't be re-added —
  // this catches both API-loaded and cards added earlier in this session.
  const existingMarketKeys = useMemo(() => {
    if (!addCardTabId) return new Set<string>();
    return new Set(
      (cardsByTabId[addCardTabId] ?? []).map(
        (card) => `${card.market_id}::${card.specifier ?? ""}`
      )
    );
  }, [addCardTabId, cardsByTabId]);

  const isOptionAlreadyAdded = (option: FutureMarketOption) =>
    existingMarketKeys.has(`${option.market_id}::${option.specifier ?? ""}`);

  const [deleteTarget, setDeleteTarget] = useState<CardTarget | null>(null);

  // The full market list comes from the autocomplete API; the search box just
  // filters it client-side.
  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return marketOptions;
    return marketOptions.filter((option) =>
      option.name.toLowerCase().includes(query)
    );
  }, [searchQuery, marketOptions]);

  // Same StrictMode-safe pattern as FutureMarketTabs / AllSports: keep tbody
  // DOM nodes in a ref map, and create Sortable in a layout effect so the
  // mount → cleanup → remount cycle always leaves a live instance. Creating
  // inside a callback ref was destroyed on StrictMode unmount and not
  // reattached until a later parent re-render (e.g. tab drag).
  const tbodyRefs = useRef<Record<string, HTMLTableSectionElement | null>>({});
  const sortableRefs = useRef<Record<string, Sortable | null>>({});
  const onReorderCardsRef = useRef(onReorderCards);
  onReorderCardsRef.current = onReorderCards;

  // Membership only — Sortable tracks row DOM itself, so card-list edits
  // must not tear down the instance mid-interaction.
  const tabIdsKey = tabs.map((tab) => tab.id).join("|");

  useLayoutEffect(() => {
    Object.values(sortableRefs.current).forEach(destroySortable);
    sortableRefs.current = {};

    Object.keys(tbodyRefs.current).forEach((tabId) => {
      if (!tabs.some((tab) => tab.id === tabId)) {
        delete tbodyRefs.current[tabId];
      }
    });

    tabs.forEach((tab) => {
      const el = tbodyRefs.current[tab.id];
      if (!el) return;

      sortableRefs.current[tab.id] = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle",
        draggable: ".card-row",
        onEnd: () => {
          const orderedEntityIds = Array.from(
            el.querySelectorAll<HTMLTableRowElement>(".card-row")
          )
            .map((node) => node.getAttribute("data-entity-id"))
            .filter((id): id is string => Boolean(id));
          if (orderedEntityIds.length) {
            onReorderCardsRef.current(tab.id, orderedEntityIds);
          }
        },
      });
    });

    return () => {
      Object.values(sortableRefs.current).forEach(destroySortable);
      sortableRefs.current = {};
    };
    // tabIdsKey tracks which sections exist; tabs is read for the loop body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIdsKey]);

  const handleOpenAddCard = (tabId: string) => {
    setAddCardTabId(tabId);
    setSearchQuery("");
    setSelectedOptions([]);
    onAddCardOpenChange?.(true);
  };

  const closeAddCardModal = () => {
    setAddCardTabId(null);
    setSearchQuery("");
    setSelectedOptions([]);
    onAddCardOpenChange?.(false);
  };

  const handleAddCard = () => {
    if (!addCardTabId || selectedOptions.length === 0) return;
    onAddCard(addCardTabId, selectedOptions);
    closeAddCardModal();
  };

  const handleDeleteCard = () => {
    if (!deleteTarget || isDeletingCard) return;
    onDeleteCard(deleteTarget.tabId, deleteTarget.card.entity_id);
    setDeleteTarget(null);
  };

  return (
    <div className="futures-market-cards" data-testid="future-market-cards">
      <div className="em-section-header em-tab-cards-intro">
        <div>
          <h3>{AppStrings.FUTURES_MARKETS}</h3>
          <p>{AppStrings.FUTURES_MANAGE_MARKET_TITLES_AND_THEIR_PLACEMENT}</p>
        </div>
        <span />
      </div>

      {tabs.map((tab) => {
        const tabCards = cardsByTabId[tab.id] ?? [];
        return (
          <div
            key={tab.id}
            className="em-tab-cards-section"
            data-testid={`future-cards-section-${tab.id}`}
          >
            <div className="em-section-header">
              <h4 className="em-tab-name">{tab.ename}</h4>
              <button
                className="em-primary"
                onClick={() => handleOpenAddCard(tab.id)}
              >
                {`+ ${AppStrings.FUTURES_ADD_MARKET}`}
              </button>
            </div>
            <div className="em-table-wrapper">
              <table className="em-subtable-table em-cards-table">
                <thead>
                  <tr>
                    <th>{`# ${AppStrings.ORDER}`}</th>
                    <th>{AppStrings.ID}</th>
                    <th>{AppStrings.ORIGINAL_NAME}</th>
                    <th>{AppStrings.ACTIONS}</th>
                  </tr>
                </thead>
                <tbody
                  ref={(el) => {
                    tbodyRefs.current[tab.id] = el;
                  }}
                >
                  {tabCards.length === 0 ? (
                    <tr className="em-empty-row">
                      <td colSpan={4} className="em-empty">
                        {AppStrings.THERE_ARE_NO_MARKETS_FOR_THIS_MARKET_TAB}
                      </td>
                    </tr>
                  ) : (
                    tabCards.map((card, idx) => (
                      <tr
                        className="card-row"
                        key={card.entity_id}
                        data-entity-id={card.entity_id}
                        data-testid={`future-card-row-${card.entity_id}`}
                      >
                        <td className="order-cell">
                          <div className="order-cell-inner">
                            <img
                              className="drag-handle"
                              src={ClipPath}
                              alt={AppStrings.DRAG}
                            />
                            <div className="order-pill">{idx + 1}</div>
                          </div>
                        </td>
                        <td>{card.market_id}</td>
                        <td>{card.original_name}</td>
                        <td className="actions">
                          <button
                            className="em-icon-btn danger"
                            title={`${AppStrings.DELETE} ${AppStrings.MARKET}`}
                            disabled={isDeletingCard}
                            onClick={() =>
                              setDeleteTarget({ tabId: tab.id, card })
                            }
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <Modal
        closable={false}
        footer={null}
        width={600}
        open={!!addCardTabId}
        onCancel={closeAddCardModal}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">
                {AppStrings.FUTURES_ADD_MARKET}
              </h2>
            </div>
            <X onClick={closeAddCardModal} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input
                id="futureCardSearch"
                placeholder={AppStrings.START_TYPING_TO_SEARCH}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="results" role="listbox">
                {isMarketOptionsLoading ? (
                  <div className="empty-mini" data-testid="future-card-options-loading">
                    {AppStrings.LOADING_MARKETS___}
                  </div>
                ) : isMarketOptionsError ? (
                  <div className="empty-mini" data-testid="future-card-options-error">
                    {AppStrings.FAILED_TO_LOAD_MARKETS}
                  </div>
                ) : (
                  <>
                    {filteredOptions.map((option) => {
                      const alreadyAdded = isOptionAlreadyAdded(option);
                      const selected = isOptionSelected(option.entity_id);
                      const handleToggle = () => {
                        if (alreadyAdded) return;
                        toggleOption(option);
                      };
                      return (
                        <div
                          key={option.entity_id}
                          data-testid={`future-card-option-${option.entity_id}`}
                          className={`result-item ${
                            selected ? "selected" : ""
                          } ${alreadyAdded ? "future-option-added" : ""}`}
                          role="option"
                          aria-selected={selected}
                          aria-disabled={alreadyAdded}
                          tabIndex={alreadyAdded ? -1 : 0}
                          onClick={handleToggle}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleToggle();
                            }
                          }}
                        >
                          <div className="result-main">
                            <span className="result-name">{option.name}</span>
                            {/* MARKET stays gold to match EventMarkets; GAME
                                uses blue so the two types are visually
                                distinct. */}
                            <Tag
                              color={option.type === "MARKET" ? "gold" : "blue"}
                            >
                              {option.type === "MARKET"
                                ? AppStrings.MARKET
                                : AppStrings.GAME}
                            </Tag>
                            {alreadyAdded && (
                              <Tag color="green">{AppStrings.ADDED}</Tag>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {filteredOptions.length === 0 && (
                      <div className="empty-mini">{AppStrings.NO_RESULTS}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={closeAddCardModal}>
              {AppStrings.BACK}
            </button>
            <button
              className={`submit-button ${
                selectedOptions.length === 0 ? "disabled" : ""
              }`}
              disabled={selectedOptions.length === 0}
              onClick={handleAddCard}
            >
              {selectedOptions.length > 0
                ? `${AppStrings.ADD} (${selectedOptions.length})`
                : AppStrings.ADD}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={500}
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">
                {`${AppStrings.DELETE} ${AppStrings.MARKET}`}
              </h2>
            </div>
            <X onClick={() => setDeleteTarget(null)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <p>
              {
                AppStrings.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_MARKET_FROM_THE_TAB
              }
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => setDeleteTarget(null)}
            >
              {AppStrings.BACK}
            </button>
            <button
              className={`submit-button delete-button ${
                isDeletingCard ? "disabled" : ""
              }`}
              disabled={isDeletingCard}
              onClick={handleDeleteCard}
            >
              {isDeletingCard ? AppStrings.DELETING___ : AppStrings.DELETE}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FutureMarketCards;
